import { useEffect, useRef, useState } from 'react';
import type { DroneMarkerHandle, HavorixMap, TrailHandle } from '../../../lib/map';
import { createBaseMap } from '../../../lib/map';
import type { FleetStatus } from '../../../lib/droneStatus';
import { useIsDark } from '../../../hooks/useTheme';
import { Button } from '../../../components/Button';

export interface FleetMarker {
  id: string;
  lng: number;
  lat: number;
  headingDeg: number | null;
  status: FleetStatus;
  label: string;
  /** 客户端累积的实时航迹（仅页面打开期间存在，WGS84） */
  trail: readonly [number, number][];
}

interface FleetMapProps {
  markers: readonly FleetMarker[];
  selectedId: string | null;
  onSelect: (droneId: string) => void;
  className?: string;
}

/**
 * 机队地图：显示全部无人机的位置、机头朝向与实时航迹。
 *
 * 底图是异步加载的（高德 JSAPI 按需注入），所以地图实例放在 state 里：
 * 建图完成的瞬间会重新跑一次同步副作用，把标记与航迹补上。
 * 组件只传 WGS84 坐标，GCJ-02 转换全部由 `lib/map.ts` 内部完成。
 */
export function FleetMap({ markers, selectedId, onSelect, className = '' }: FleetMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<HavorixMap | null>(null);
  const markerHandles = useRef(new Map<string, DroneMarkerHandle>());
  const trailHandles = useRef(new Map<string, TrailHandle>());
  const onSelectRef = useRef(onSelect);
  const didFitRef = useRef(false);
  const dark = useIsDark();

  onSelectRef.current = onSelect;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;
    let created: HavorixMap | null = null;

    void createBaseMap(container, {
      dark: document.documentElement.classList.contains('dark'),
      zoom: 11,
    }).then((instance) => {
      if (!instance) return;
      // 建图期间组件已卸载：直接销毁，避免留下一个没人管的实例
      if (disposed) {
        instance.destroy();
        return;
      }
      created = instance;
      setMap(instance);
    });

    return () => {
      disposed = true;
      created?.destroy();
      created = null;
      markerHandles.current.clear();
      trailHandles.current.clear();
      didFitRef.current = false;
      setMap(null);
    };
  }, []);

  useEffect(() => {
    map?.setDark(dark);
  }, [map, dark]);

  useEffect(() => {
    if (!map) return;

    const seen = new Set<string>();

    for (const marker of markers) {
      if (!Number.isFinite(marker.lng) || !Number.isFinite(marker.lat)) continue;
      seen.add(marker.id);

      const appearance = {
        status: marker.status,
        headingDeg: marker.headingDeg,
        selected: marker.id === selectedId,
        label: marker.label,
      };

      let handle = markerHandles.current.get(marker.id);
      if (!handle) {
        handle = map.addMarker(appearance, marker.lng, marker.lat);
        handle.onClick(() => onSelectRef.current(marker.id));
        markerHandles.current.set(marker.id, handle);
      } else {
        handle.setAppearance(appearance);
        handle.setPosition(marker.lng, marker.lat);
      }

      let trail = trailHandles.current.get(marker.id);
      if (!trail) {
        trail = map.createTrail();
        trailHandles.current.set(marker.id, trail);
      }
      trail.setPath(marker.trail);
    }

    for (const [id, handle] of markerHandles.current) {
      if (seen.has(id)) continue;
      handle.remove();
      markerHandles.current.delete(id);
      trailHandles.current.get(id)?.remove();
      trailHandles.current.delete(id);
    }

    if (!didFitRef.current && markers.length > 0) {
      didFitRef.current = true;
      map.fitBounds(markers.map((marker) => [marker.lng, marker.lat] as const));
    }
  }, [map, markers, selectedId]);

  return (
    <div className={['relative overflow-hidden', className].join(' ')}>
      <div ref={containerRef} className="havorix-map-container absolute inset-0" />
      <div className="pointer-events-none absolute left-2 top-2 z-10 flex items-center gap-1.5">
        <Button
          size="sm"
          variant="default"
          className="pointer-events-auto bg-surface-1/95 backdrop-blur"
          onClick={() => {
            if (!map || markers.length === 0) return;
            map.fitBounds(markers.map((marker) => [marker.lng, marker.lat] as const));
          }}
        >
          全览
        </Button>
      </div>
    </div>
  );
}
